'use client'

import type { TextField } from '@payloadcms/plugin-form-builder/types'
import type { FieldErrorsImpl, FieldValues, UseFormRegister } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import React, { useState, useEffect } from 'react'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Error } from '../Error'
import { Width } from '../Width'
import { useFormFieldValue } from '../hooks/useFormFieldValue'
import { Locale, TypedLocale } from 'payload'
import { useFormContext } from 'react-hook-form'

export const DateField: React.FC<
  TextField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
    locale: TypedLocale
  }
> = ({ name, defaultValue, errors, label, register, required, width, locale }) => {
  const [urlValue, setUrlValue] = useFormFieldValue(name, defaultValue)
  const { setValue, watch } = useFormContext()
  const [open, setOpen] = useState(false)

  // Watch the form value to keep it in sync
  const formValue = watch(name)
  const selected = urlValue ? new Date(urlValue) : undefined

  // Ensure form state is updated when URL value changes
  useEffect(() => {
    if (urlValue && urlValue !== formValue) {
      setValue(name, urlValue, { shouldValidate: true })
    }
  }, [urlValue, formValue, setValue, name])

  // Ensure URL value is set when form value changes (for external updates)
  useEffect(() => {
    if (formValue && formValue !== urlValue) {
      setUrlValue(formValue, { shallow: true, scroll: false })
    }
  }, [formValue, urlValue, setUrlValue])

  return (
    <Width width={width}>
      <Label htmlFor={name}>
        {label}
        {required && (
          <span className="required">
            * <span className="sr-only">(required)</span>
          </span>
        )}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start  border-stone-200 bg-white text-left text-base font-normal text-gray-900 transition-all duration-300 placeholder:text-stone-400 hover:border-stone-400 focus:border-stone-500 focus:ring-stone-200"
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-antonie-brown" />
            {selected ? (
              format(selected, 'dd.MM.yyyy')
            ) : (
              <span className="text-stone-400">tt.mm.jjjj</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            locale={locale as Partial<Locale>}
            onSelect={(date) => {
              if (date) {
                const formatted = format(date, 'yyyy-MM-dd')
                setUrlValue(formatted, { shallow: true, scroll: false })
                setValue(name, formatted, { shouldValidate: true })
              } else {
                setUrlValue(null, { shallow: true, scroll: false })
                setValue(name, '', { shouldValidate: true })
              }
              setOpen(false)
            }}
            className="rounded-md border p-2"
          />
        </PopoverContent>
      </Popover>
      {/* Hidden input for react-hook-form */}
      <input type="hidden" value={urlValue || ''} {...register(name, { required })} />
      {errors[name] && <Error name={name} locale={locale} />}
    </Width>
  )
}
